import { BrowserModule, HAMMER_GESTURE_CONFIG, HammerModule } from '@angular/platform-browser';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from '../app/app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RestClientService } from '../services/rest-client.service';
import { UserTokenService } from '../services/user-service/user-token.service';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { HttpConfigInterceptor } from '../services/http-config-interceptor';
import { environment } from '../environments/environment';
import { RouterModule } from '@angular/router';
import { CONTACTS_DATA_SERVICE, ContactsDataService } from '../services/contacts/contacts-data.service';
import { MockContactsDataService } from '../services/contacts/mock-contacts-data.service';
import { ApiContactsDataService } from '../services/contacts/api-contacts-data.service';
import { LEADS_DATA_SERVICE, LeadsDataService } from '../services/leads/leads-data.service';
import { MockLeadsDataService } from '../services/leads/mock-leads-data.service';
import { ApiLeadsDataService } from '../services/leads/api-leads-data.service';
import { RESOURCES_DATA_SERVICE, ResourcesDataService } from '../services/resources/resources-data.service';
import { ApiResourcesDataService } from '../services/resources/api-resources-data.service';
import { MockResourcesDataService } from '../services/resources/mock-resources-data.service';
import { PROFILE_DATA_SERVICE, ProfileDataService } from '../services/profile/profile-data.service';
import { ApiProfileDataService } from '../services/profile/api-profile-data.service';
import { MockProfileDataService } from '../services/profile/mock-profile-data.service';
import { HammerConfig } from '../services/hummer.service';
import { ACTION_DATA_SERVICE, ActionDataService } from '../services/action/data.service';
import { ApiActionDataService } from '../services/action/api-data.service';
import { UserProfileShareService } from '../services/shared-service/user-profile-share.service';
import { NgxsModule } from '@ngxs/store';
import { LoaderState } from '../app/store/loader/loader.state';
import localeDe from '@angular/common/locales/de';
import { registerLocaleData } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ConnectService } from "../services/connect-two-component/connect.service";
import { DATA_TRANSFER_HELPER, DataTransfer } from "../helper/dataTransfer.helper";
import { InfoSideBarEmailService } from "../app/page-modules/contact-details/info-side-bar/info-side-bar-email.service";
import { FileDownloadModule } from "./file-download.module";
import { LeadActionState } from "../app/store/lead-action/lead-action.state";
import { CheckedLeadsState } from "../app/store/checked-leads/checked-leads.state";
import { SortService } from "../services/sort/sort.service";
import { CLOUD_DATA_SERVICE, CloudDataService } from '../services/cloud/cloud-data.service';
import { MockCloudDataService } from '../services/cloud/mock-cloud-data.service';
import { ApiCloudDataService } from '../services/cloud/api-cloud-data.service';
import { CompactViewState } from '../app/store/compact-view/compact-view.state';
import { MockActionDataService } from '../services/action/mock-action-data.service';
import { ToastrModule } from 'ngx-toastr';

registerLocaleData(localeDe);

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    FileDownloadModule,
    BrowserAnimationsModule,
    RouterModule,
    HammerModule,
    FormsModule,
    ToastrModule.forRoot(),
    NgxsModule.forRoot([LoaderState, LeadActionState, CheckedLeadsState, CompactViewState], { developmentMode: !environment.production })
  ],
  providers: [
    RestClientService,
    UserTokenService,
    UserProfileShareService,
    SortService,
    InfoSideBarEmailService,
    ConnectService,
    {
      provide: LOCALE_ID,
      useValue: 'de-DE'
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpConfigInterceptor,
      multi: true
    },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: HammerConfig,
    },
    {
      provide: DATA_TRANSFER_HELPER,
      useClass: DataTransfer,
    },
    { provide: CONTACTS_DATA_SERVICE, useFactory: contactsServiceFactory, deps: [RestClientService] },
    { provide: CLOUD_DATA_SERVICE, useFactory: cloudServiceFactory, deps: [RestClientService] },
    { provide: LEADS_DATA_SERVICE, useFactory: leadsServiceFactory, deps: [RestClientService] },
    { provide: RESOURCES_DATA_SERVICE, useFactory: resourcesServiceFactory, deps: [RestClientService] },
    { provide: PROFILE_DATA_SERVICE, useFactory: profileServiceFactory, deps: [RestClientService] },
    { provide: ACTION_DATA_SERVICE, useFactory: actionServiceFactory, deps: [RestClientService] },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [CONTACTS_DATA_SERVICE],
      multi: true,
    },
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}


/** Provides the contacts data service. */
export function contactsServiceFactory( client: RestClientService ): ContactsDataService {
  if ( environment.dataProvider === 'mock' ) {
    return new MockContactsDataService();
  }

  return new ApiContactsDataService(client);
}

/** Provides the contacts data service. */
export function cloudServiceFactory( client: RestClientService ): CloudDataService {
  if ( environment.dataProvider === 'mock' ) {
    return new MockCloudDataService();
  }

  return new ApiCloudDataService(client);
}

/** Provides the leads data service. */
export function leadsServiceFactory( client: RestClientService ): LeadsDataService {
  if ( environment.dataProvider === 'mock' ) {
    return new MockLeadsDataService();
  }

  return new ApiLeadsDataService(client);
}


/** Provides the resources data service. */
export function resourcesServiceFactory( client: RestClientService ): ResourcesDataService {
  if ( environment.dataProvider === 'mock' ) {
    return new MockResourcesDataService();
  }

  return new ApiResourcesDataService(client);
}

/** Provides the profile data service. */
export function profileServiceFactory( client: RestClientService ): ProfileDataService {
  if ( environment.dataProvider === 'mock' ) {
    return new MockProfileDataService();
  }

  return new ApiProfileDataService(client);
}

/** Provides the profile data service. */
export function actionServiceFactory( client: RestClientService ): ActionDataService {
  if ( environment.dataProvider === 'mock' ) {
    return new MockActionDataService();
  }

  return new ApiActionDataService(client);
}


export function appInitializerFactory( contactsDataService: ContactsDataService ) {
  return (): Promise<any> => {
    return new Promise(( resolve, reject ) => {

      setTimeout(() => {

        return contactsDataService.healthCheck().subscribe(( { data, success, error } ) => {
          if ( error ) {
            AppComponent.maintenanceMode = true;
          }
          // @ts-ignore
          resolve();
        });
      }, 100);

    });
  };
}
